import React, { useEffect, useState, useRef } from 'react'
import { useRouter } from 'next/router'
import cx from 'classnames'
import { useWindowSize } from 'utils/hooks'
import { disableBodyScroll } from 'utils'
import { IconNameType } from '~types'
import { NavigationContextType, NavigationHeaderProps } from './types'
import styles from './NavigationHeader.module.scss'
import { NavDesktop } from './elements/NavDesktop/NavDesktop'
import { NavMobile } from './elements/NavMobile/NavMobile'
import BackgroundGradientBlur from './elements/BackgroundGradientBlur/BackgroundGradientBlur'
import { NavigationProvider } from './NavigationContext'
import { fireAnalyticsEvent } from '~analyticsUtils'

const NavigationHeader = ({
  brandLogo,
  navigationMenuItems = [],
  buttonTitle,
  buttonUrl,
  buttonIcon,
  isTargetBlank: buttonIsTargetBlank,
  endNavItem,
  endNavItemUrl
}: NavigationHeaderProps) => {
  const { asPath } = useRouter()
  const { isBreakpoint: isMobileView, windowSizeCalculation } = useWindowSize('md-plus', '<')
  const ref = useRef(null)
  const [navDrawerIsOpen, setNavDrawerIsOpen] = useState(false)
  const [openSubMenuIndex, setOpenSubMenuIndex] = useState<number | null>(null)

  // Toggle mobile drawer
  const toggleNavDrawer = () => setNavDrawerIsOpen(prev => !prev)

  const fireAnalyticsClickEvent = (url: string, title: string, index: string = '1') => {
    fireAnalyticsEvent(url, asPath, {
      event: 'click_nav',
      clickTracking: `global_navigation:${title}`,
      clickType: 'navigation',
      linkLocation: 'header',
      linkModule: 'global_navigation',
      linkPosition: index
    })
  }

  const context: NavigationContextType = {
    brandLogo,
    navigationMenuItems,
    ctaButton: {
      isTargetBlank: buttonIsTargetBlank,
      iconName: buttonIcon as IconNameType | undefined,
      url: buttonUrl,
      title: buttonTitle
    },
    endNavItem: {
      title: endNavItem,
      url: endNavItemUrl
    },
    navDrawerIsOpen,
    toggleNavDrawer,
    isMobileView: !!isMobileView, // !!isMobileView resolves ts error when null is sent
    fireAnalyticsClickEvent,
    setOpenSubMenuIndex,
    openSubMenuIndex
  }

  // Disable body scroll when drawer is open
  useEffect(() => {
    disableBodyScroll(navDrawerIsOpen)
  }, [navDrawerIsOpen])

  if (!windowSizeCalculation) return null

  return (
    <NavigationProvider value={context}>
      <header
        ref={ref}
        className={cx(
          styles['header-global'],
          styles[isMobileView ? 'header-global--mobile' : 'header-global--desktop'],
          styles[navDrawerIsOpen && isMobileView ? 'drawer-open' : '']
        )}
      >
        {isMobileView ? <NavMobile /> : <NavDesktop />}
        <BackgroundGradientBlur />
      </header>
    </NavigationProvider>
  )
}

export default NavigationHeader
